/*
 * Rebel Athletic — Collection Attribution capture (theme-safe)
 * --------------------------------------------------------------------------
 * v2.1 — adds PAGE-LOAD attribution write.
 *
 * v2 only wrote the order-level _sales_rep at add-to-cart. Gap (RPS1219):
 * a returning buyer with a carried-over cart lands on a program collection
 * (e.g. from the gym's Facebook post) and checks out WITHOUT re-adding —
 * no add event, no write, order arrives unattributed despite the buyer
 * standing on the collection page seconds before paying.
 *
 * v2.1: when a page loads with program-collection context (current page is a
 * program collection, or context persisted earlier this session), write the
 * order-level attributes immediately via ONE /cart/update.js call, guarded by
 * a session marker so the same values are never re-sent. Add-to-cart still
 * writes unconditionally (authoritative; last touch wins, consistent with
 * last-add-wins).
 *
 * Still NO patching of window.fetch / XMLHttpRequest. Nothing intercepts the
 * theme's own requests (Stiletto instant navigation untouched). Fails open
 * everywhere.
 */
(function () {
  'use strict';

  var PREFIX = '_source_';
  var STORE_KEY = 'rebel_last_collection';
  var WRITTEN_KEY = 'rebel_attrs_written';

  // ---- source resolution --------------------------------------------------
  function readCurrent() {
    var c = (window.RebelAttribution && window.RebelAttribution.current) || null;
    return (c && c.collection_handle) ? c : null;
  }

  function readStored() {
    try {
      var raw = sessionStorage.getItem(STORE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function persistCurrent() {
    var c = readCurrent();
    if (c) {
      try { sessionStorage.setItem(STORE_KEY, JSON.stringify(c)); } catch (e) {}
    }
  }

  function getSource() {
    return readCurrent() || readStored() || null;
  }

  // ---- per-line properties: every populated key -> _source_<key> -----------
  function buildProps() {
    var s = getSource();
    if (!s || !s.collection_handle) return null;
    var p = {};
    Object.keys(s).forEach(function (k) {
      var v = s[k];
      if (v !== null && v !== undefined && String(v).length) {
        p[PREFIX + k] = String(v);
      }
    });
    return p;
  }

  // ---- order-level single rep (last touch wins) ----------------------------
  function buildOrderAttrs() {
    var s = getSource();
    if (!s || !s.sales_rep) return null; // only overwrite when we know the rep
    return {
      '_sales_rep': String(s.sales_rep),
      '_sf_account_number': s.salesforce_account_number ? String(s.salesforce_account_number) : '',
      '_sf_account_name': s.salesforce_account_name ? String(s.salesforce_account_name) : '',
      '_source_collection': s.collection_handle ? String(s.collection_handle) : ''
    };
  }

  function attrsSig(attrs) {
    return attrs['_sales_rep'] + '|' + attrs['_source_collection'];
  }

  // A plain call to OUR endpoint. Not a network interceptor; the theme's own
  // requests are untouched.
  function writeOrderRep() {
    var attrs = buildOrderAttrs();
    if (!attrs || !window.fetch) return;
    try {
      window.fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      }).then(function () {}, function () {});
      try { sessionStorage.setItem(WRITTEN_KEY, attrsSig(attrs)); } catch (e) {}
    } catch (e) {}
  }

  // Page-load write, session-guarded: only fires when the attribute values
  // differ from what this session already wrote. Closes the carried-cart gap
  // (buyer lands on a collection with items already in cart, never re-adds).
  function maybeWriteOrderRep() {
    var attrs = buildOrderAttrs();
    if (!attrs) return;
    try {
      if (sessionStorage.getItem(WRITTEN_KEY) === attrsSig(attrs)) return;
    } catch (e) {}
    writeOrderRep();
  }

  // ---- add-to-cart detection ----------------------------------------------
  function isCartAdd(action) {
    if (!action) return false;
    try {
      return /\/cart\/add(\.js)?$/.test(String(action).split('?')[0]);
    } catch (e) { return false; }
  }

  function findAddForm(node) {
    // Walk up from an element to the nearest <form> that posts to /cart/add.
    var el = node;
    while (el && el !== document) {
      if (el.tagName === 'FORM') {
        var action = el.getAttribute('action') || el.action;
        return isCartAdd(action) ? el : null;
      }
      el = el.parentNode;
    }
    return null;
  }

  function clearStale(form) {
    var stale = form.querySelectorAll('input[data-rebel-attr="1"]');
    Array.prototype.forEach.call(stale, function (n) {
      if (n.parentNode) n.parentNode.removeChild(n);
    });
  }

  function injectHidden(form) {
    var props = buildProps();
    if (!props) return;
    clearStale(form);
    Object.keys(props).forEach(function (k) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + k + ']';
      input.value = props[k];
      input.setAttribute('data-rebel-attr', '1');
      form.appendChild(input);
    });
  }

  function stamp(form) {
    if (!form) return;
    injectHidden(form);  // line-item _source_* props (serialized by the theme)
    writeOrderRep();     // order-level _sales_rep via /cart/update.js (authoritative)
  }

  // Capture-phase hooks run BEFORE the theme's own handlers, so the hidden
  // inputs are present when the theme serializes the form — whether it submits
  // natively or adds via its own AJAX. We never touch the theme's requests.
  document.addEventListener('submit', function (e) {
    try {
      var form = e.target;
      if (form && form.tagName === 'FORM') {
        var action = form.getAttribute('action') || form.action;
        if (isCartAdd(action)) stamp(form);
      }
    } catch (err) {}
  }, true);

  // Covers themes whose add-to-cart button is a click handler that never fires
  // a real form submit event (quick-add, sticky bars, AJAX buttons).
  document.addEventListener('click', function (e) {
    try {
      var t = e.target;
      if (!t) return;
      var btn = (t.closest ? t.closest('[type="submit"], [name="add"], button, a') : null) || t;
      var form = findAddForm(btn);
      if (form) stamp(form);
    } catch (err) {}
  }, true);

  // ---- on every page: persist context, then write if it's new --------------
  function onReady() {
    persistCurrent();
    maybeWriteOrderRep();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  // Debug helpers.
  window.RebelAttribution = window.RebelAttribution || {};
  window.RebelAttribution.getSource = getSource;
  window.RebelAttribution.buildProps = buildProps;
  window.RebelAttribution.buildOrderAttrs = buildOrderAttrs;
  window.RebelAttribution.maybeWriteOrderRep = maybeWriteOrderRep;
})();
